import React from 'react';
import { Link } from 'react-router-dom';
import { BTN } from '../constants/buttons';

/**
 * Shared empty-state component for any list/dashboard with no data.
 *
 * Use this everywhere instead of rolling per-page JSX. Solves the recurring
 * problems we found in the empty-state audit:
 *   - inconsistent dark-mode coverage
 *   - mismatched copy tone across pages
 *   - missing CTAs that leave users wondering what to do
 *
 * Usage examples:
 *
 *   // Page-level "you have no orders yet" with a primary CTA
 *   <EmptyState
 *     icon={<DocumentIcon />}
 *     title="No orders yet"
 *     description="Get started by creating your first order."
 *     cta={{ label: 'Create your first order', to: '/orders/new' }}
 *   />
 *
 *   // Filter / search miss — no CTA, smaller footprint
 *   <EmptyState
 *     size="sm"
 *     title="No matching orders"
 *     description="Try selecting more statuses."
 *   />
 *
 *   // "All caught up" success-flavoured empty state — no CTA needed
 *   <EmptyState
 *     icon={<CheckCircleIcon />}
 *     title="All caught up!"
 *     description="Nothing waiting on your approval."
 *   />
 *
 * Props:
 *   icon         optional React node (typically an SVG sized via Tailwind classes inside)
 *   title        required, the headline
 *   description  required, one-line context
 *   cta          optional { label, to | onClick, icon? } — pass either `to` (router Link) or `onClick` (button)
 *   size         'md' (default — page-level) or 'sm' (inline / list-area)
 *   className    extra classes for the outer card
 *   testId       optional data-testid for the outer card (defaults to "empty-state")
 */
export default function EmptyState({
  icon,
  title,
  description,
  cta,
  size = 'md',
  className = '',
  testId = 'empty-state',
}) {
  const padding = size === 'sm' ? 'p-6' : 'p-10';
  const titleClass = size === 'sm' ? 'text-base' : 'text-lg';
  const descSpacing = cta ? 'mb-5' : '';
  const defaultIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`mx-auto ${size === 'sm' ? 'h-10 w-10' : 'h-14 w-14'} text-gray-300 dark:text-gray-600 mb-4`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );

  return (
    <div
      data-testid={testId}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 ${padding} text-center ${className}`.trim()}
    >
      {icon !== null && (icon || defaultIcon)}
      <p className={`text-gray-700 dark:text-gray-200 ${titleClass} font-semibold mb-2`}>
        {title}
      </p>
      {description && (
        <p className={`text-gray-500 dark:text-gray-400 text-sm ${descSpacing}`}>
          {description}
        </p>
      )}
      {cta && <CtaButton cta={cta} />}
    </div>
  );
}

function CtaButton({ cta }) {
  const content = (
    <>
      {cta.icon || (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      )}
      <span>{cta.label}</span>
    </>
  );
  // Larger padding than BTN.primary's default; visually distinct as the
  // page's primary action without being aggressive.
  const className = `${BTN.primary} px-5 py-2.5 text-sm`;
  if (cta.to) {
    return (
      <Link to={cta.to} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button type="button" onClick={cta.onClick} className={className}>
      {content}
    </button>
  );
}
